/**
 * PATH: apps/qa/src/pages/report-editor/constants.ts
 * PURPOSE: Section configuration for the report editor
 */

import {
  FileText, BookOpen, Building2, AlertTriangle,
  Camera, MessageSquare, Users, ListChecks,
} from 'lucide-react'

export const SECTION_ORDER = [
  'cover', 'executive_summary', 'scope', 'findings',
  'ncrs', 'evidence', 'recommendations', 'signoffs',
]

export const SECTION_LABELS: Record<string, string> = {
  cover: 'Cover Page',
  executive_summary: 'Executive Summary',
  scope: 'Inspection Scope',
  findings: 'Findings by Area',
  ncrs: 'NCRs & Defects',
  evidence: 'Evidence & Photos',
  recommendations: 'Recommendations',
  signoffs: 'Sign-offs',
}

export const SECTION_DESCRIPTIONS: Record<string, string> = {
  cover: 'Project info, inspection details, date, and prepared by',
  executive_summary: 'High-level overview of findings and assessment',
  scope: 'What was inspected — floors, areas, building classes',
  findings: 'Detailed findings per floor and area with checklist status',
  ncrs: 'Non-conformance reports and defect summary table',
  evidence: 'Photos, documents, and notes attached to the inspection',
  recommendations: 'Your recommendations based on inspection findings',
  signoffs: 'Sign-off table for QA Manager, PM, and Builder Rep',
}

export const SECTION_ICONS: Record<string, typeof FileText> = {
  cover: FileText,
  executive_summary: BookOpen,
  scope: Building2,
  findings: ListChecks,
  ncrs: AlertTriangle,
  evidence: Camera,
  recommendations: MessageSquare,
  signoffs: Users,
}

export const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  review: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  final: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  exported: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
}

export function getSectionLabel(sectionType: string) {
  if (SECTION_LABELS[sectionType]) return SECTION_LABELS[sectionType]
  if (sectionType.startsWith('floor_')) return sectionType.slice(6).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
  return sectionType.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function getSectionDescription(sectionType: string) {
  return SECTION_DESCRIPTIONS[sectionType] || (sectionType.startsWith('floor_') ? 'Generated floor findings, checklist outcomes, and evidence.' : 'QA report section')
}
